// HTML inputs already use the iOS/Android keyboard. Keep native controls and
// existing autocomplete/IME behavior; only supply missing keyboard hints.
export function keyboardMode(type: string, name: string, multiline = false): string {
  if (multiline) return 'text';
  if (['tel','email','url','number'].includes(type)) return type === 'number' ? 'decimal' : type;
  if (type !== 'text' && type !== 'search') return '';
  if (/(?:phone|mobile|telephone|customer_phone)/i.test(name)) return 'tel';
  if (/(?:otp|verification.?code|partner.?code|specialist.?id)/i.test(name)) return 'numeric';
  if (/(?:price|amount|fee|balance|radius)/i.test(name)) return 'decimal';
  if (type === 'search') return 'search';
  return 'text';
}

export function installSystemKeyboard() {
  const selector = 'input,textarea';
  const isText = (el: Element): el is HTMLInputElement | HTMLTextAreaElement =>
    el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement &&
    ['text','tel','email','url','search','number','password'].includes(el.type);
  const fields = (el: HTMLInputElement | HTMLTextAreaElement) => {
    // Never jump outside the current modal or form.
    const scope = el.closest('form,[role="dialog"],dialog') || el.parentElement;
    return [...(scope?.querySelectorAll(selector) || [])].filter(isText)
      .filter(x => !x.disabled && !x.readOnly && x.getClientRects().length > 0);
  };
  const configure = (el: Element) => {
    if (!isText(el)) return;
    const mode = keyboardMode(el instanceof HTMLInputElement ? el.type : 'text',
      `${el.getAttribute('name') || ''} ${el.id}`, el instanceof HTMLTextAreaElement);
    if (mode && !el.hasAttribute('inputmode')) el.inputMode = mode;
    if (!el.hasAttribute('enterkeyhint')) {
      el.enterKeyHint = el instanceof HTMLTextAreaElement ? 'enter' :
        el instanceof HTMLInputElement && el.type === 'search' ? 'search' : 'done';
      el.dataset.timKeyboardAction = 'auto';
    }
    if (['tel','email','url','numeric','decimal'].includes(mode)) {
      if (!el.hasAttribute('autocapitalize')) el.setAttribute('autocapitalize','none');
      if (!el.hasAttribute('spellcheck')) el.spellcheck = false;
    }
  };
  const scan = (root: Element | Document) => {
    if (root instanceof Element) configure(root);
    root.querySelectorAll(selector).forEach(configure);
  };
  scan(document);
  const observer = new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes)
      if (node instanceof Element) scan(node);
  });
  observer.observe(document.body,{childList:true,subtree:true});
  let frame = 0;
  const reveal = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const el = document.activeElement;
      if (!(el instanceof Element) || !isText(el) || !matchMedia('(pointer:coarse)').matches) return;
      const viewport = visualViewport;
      const top = (viewport?.offsetTop || 0) + 16;
      const bottom = top + (viewport?.height || innerHeight) - 40;
      const rect = el.getBoundingClientRect();
      if (rect.top < top || rect.bottom > bottom)
        el.scrollIntoView({block:'nearest',inline:'nearest',behavior:'instant'});
    });
  };
  document.addEventListener('focusin',event => {
    const el = event.target;
    if (!(el instanceof Element) || !isText(el)) return;
    configure(el);
    if (el.dataset.timKeyboardAction === 'auto' && !(el instanceof HTMLTextAreaElement) && el.type !== 'search') {
      const list = fields(el); el.enterKeyHint = list.indexOf(el) < list.length - 1 ? 'next' : 'done';
    }
    reveal();
  });
  document.addEventListener('keydown',event => {
    const el = event.target;
    if (event.key !== 'Enter' || event.isComposing || event.shiftKey || event.ctrlKey || event.metaKey ||
        !(el instanceof HTMLInputElement) || !isText(el) || el.type === 'search' ||
        !['next','done'].includes(el.enterKeyHint)) return;
    // An open address suggestion list owns Enter to confirm its selected place.
    if (el.getAttribute('aria-expanded') === 'true' || el.getAttribute('aria-activedescendant')) return;
    if (event.defaultPrevented) return;
    if (el.enterKeyHint === 'next') {
      const list = fields(el), next = list[list.indexOf(el)+1];
      if (next) { event.preventDefault(); next.focus(); }
    } else if (el.dataset.timKeyboardAction === 'auto' && el.type !== 'password') {
      event.preventDefault(); el.blur();
    }
  });
  visualViewport?.addEventListener('resize',reveal);
}
